import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { LiveTrackingMap } from "../LiveTrackingMap";
import { Navigation, Share2, Radio, Clock, Route } from "lucide-react";
import { toast } from "sonner";

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function gapText(a, b) {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!ms || ms < 0) return "";
  const m = Math.round(ms / 60000);
  if (m < 1) return "under a minute";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function haversineKm(a, b) {
  const R = 6371, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function trailStats(trail = []) {
  let total = 0;
  for (let i = 1; i < trail.length; i++) total += haversineKm(trail[i - 1], trail[i]);
  const n = trail.length;
  const last2 = n >= 2 ? [trail[n - 2], trail[n - 1]] : null;
  return {
    updates: n,
    totalKm: total,
    lastGap: last2 ? gapText(last2[0].at, last2[1].at) : "",
    lastKmApprox: last2 ? haversineKm(last2[0], last2[1]) : 0,
  };
}

export function LiveTracking({ lr }) {
  const [data, setData] = useState(null);
  const [routeInfo, setRouteInfo] = useState({});

  const load = useCallback(() => {
    api.get(`/lrs/${lr.id}/tracking`).then((r) => setData(r.data)).catch(() => {});
  }, [lr.id]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  async function shareLink() {
    try {
      const { data: d } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}/p/live/${d.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Live-location link copied — send it to your driver(s) on WhatsApp");
    } catch {
      toast.error("Could not create the link");
    }
  }
  async function shareWhatsApp() {
    try {
      const { data: d } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}/p/live/${d.token}`;
      const msg = `Please share your live location for delivery of ${lr.lr_number}. Open this link and tap Start:\n${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch { toast.error("Could not create the link"); }
  }

  const trackers = data?.trackers || [];

  return (
    <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-live-tracking">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="section-title flex items-center gap-2 mb-0"><Navigation className="w-4 h-4" /> Live Tracking</div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs" onClick={shareLink} data-testid="live-share-copy-btn"><Share2 className="w-3.5 h-3.5" /> Copy driver link</button>
          <button className="btn-secondary text-xs text-[#1f8a4c]" onClick={shareWhatsApp} data-testid="live-share-wa-btn"><Share2 className="w-3.5 h-3.5" /> WhatsApp</button>
        </div>
      </div>

      {data?.stopped && (
        <div className="text-xs text-[#2F6F4E] bg-[#E7F2EC] rounded px-3 py-2 mb-3" data-testid="live-tracking-ended">Delivery complete — live tracking has ended. Showing last known positions.</div>
      )}

      <LiveTrackingMap pickup={data?.pickup} delivery={data?.delivery} trackers={trackers}
        onRouteInfo={(id, info) => setRouteInfo((p) => ({ ...p, [id]: info }))} />

      <div className="mt-3">
        {trackers.length === 0 ? (
          <div className="text-sm text-[#A09E96]" data-testid="live-tracking-empty">No one is sharing location yet. Send the driver link above — each person taps Start and their pin appears here, live.</div>
        ) : (
          <div className="divide-y divide-[#F0EDE5]" data-testid="live-tracking-list">
            {trackers.map((t) => {
              const st = trailStats(t.trail || []);
              const ri = routeInfo[t.tracker_id];
              const legKm = ri?.distance_text || (st.lastKmApprox ? `${st.lastKmApprox.toFixed(1)} km (approx)` : "");
              return (
                <div key={t.tracker_id} className="py-2" data-testid={`live-tracker-${t.tracker_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium text-[#1A1A18]"><Radio className="w-3.5 h-3.5 text-[#1A73E8]" /> {t.label || "Driver"}</span>
                    <span className="text-xs text-[#68665E] flex items-center gap-1"><Clock className="w-3 h-3" /> {ago(t.updated_at)}{t.accuracy ? ` · ±${Math.round(t.accuracy)}m` : ""}</span>
                  </div>
                  {st.updates >= 2 && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-[#68665E]" data-testid={`live-tracker-legs-${t.tracker_id}`}>
                      <span className="flex items-center gap-1 text-[#C84B31] font-medium"><Route className="w-3 h-3" /> Since last update: {legKm || "—"}{st.lastGap ? ` · ${st.lastGap} gap` : ""}{ri?.duration_text ? ` · ~${ri.duration_text} drive` : ""}</span>
                      <span>Tracked total: {st.totalKm.toFixed(1)} km · {st.updates} updates</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveTracking;
